// Folder Sharing Service - Handles folder share creation and management
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';
import { throwIfManuallySuspended } from '@utils/suspension';

export interface FolderShare {
  id: string;
  folder_id: string;
  user_id: string;
  share_token: string;
  password: string | null;
  expires_at: string | null;
  max_downloads: number | null;
  download_count: number;
  views: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contact_email_override: string | null;
  instagram_url_override: string | null;
  beatstars_profile_url_override: string | null;
  show_contact: boolean;
}

export interface CreateFolderShareOptions {
  folderId: string;
  password?: string;
  expiresInDays?: number | null; // null = never expires
  maxDownloads?: number | null; // null = unlimited
  contactEmailOverride?: string | null;
  instagramUrlOverride?: string | null;
  beatstarsProfileUrlOverride?: string | null;
  showContact?: boolean;
}

export interface FolderShareResult {
  share: FolderShare;
  shareUrl: string;
}

class FolderSharingService {
  /**
   * Create a new folder share
   */
  async createFolderShare(options: CreateFolderShareOptions): Promise<FolderShareResult> {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Check if account is manually suspended (without download access)
    await throwIfManuallySuspended();

    const { folderId, password, expiresInDays, maxDownloads, contactEmailOverride, instagramUrlOverride, beatstarsProfileUrlOverride, showContact } = options;

    // Calculate expiration date
    let expiresAt: string | null = null;
    if (expiresInDays !== null && expiresInDays !== undefined) {
      const date = new Date();
      date.setDate(date.getDate() + expiresInDays);
      expiresAt = date.toISOString();
    }

    // Hash password if provided
    let hashedPassword: string | null = null;
    if (password && password.trim() !== '') {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    // Check if there's an existing active share for this folder
    const { data: existingShare } = await supabase
      .from('folder_shares')
      .select('*')
      .eq('folder_id', folderId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    let share: FolderShare;

    if (existingShare) {
      // Update existing share
      const { data: updatedShare, error: updateError } = await supabase
        .from('folder_shares')
        .update({
          password: hashedPassword,
          expires_at: expiresAt,
          max_downloads: maxDownloads,
          is_active: true,
          contact_email_override: contactEmailOverride?.trim() || null,
          instagram_url_override: instagramUrlOverride?.trim() || null,
          beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
          show_contact: showContact ?? true,
        })
        .eq('id', existingShare.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update share: ${updateError.message}`);
      }

      share = updatedShare as FolderShare;
    } else {
      // Generate unique token (with retry logic for uniqueness)
      let attempts = 0;
      let newShare: FolderShare | null = null;
      const maxAttempts = 5;

      while (!newShare && attempts < maxAttempts) {
        const token = this.generateToken();
        
        const { data, error: createError } = await supabase
          .from('folder_shares')
          .insert({
            folder_id: folderId,
            user_id: user.id,
            share_token: token,
            password: hashedPassword,
            expires_at: expiresAt,
            max_downloads: maxDownloads,
            is_active: true,
            download_count: 0,
            views: 0,
            contact_email_override: contactEmailOverride?.trim() || null,
            instagram_url_override: instagramUrlOverride?.trim() || null,
            beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
            show_contact: showContact ?? true,
          })
          .select()
          .single();

        if (createError) {
          // If it's a unique constraint error, try again with a new token
          if (createError.code === '23505' && attempts < maxAttempts - 1) {
            attempts++;
            continue;
          }
          throw new Error(`Failed to create share: ${createError.message}`);
        }

        newShare = data as FolderShare;
      }

      if (!newShare) {
        throw new Error('Failed to create share after multiple attempts');
      }

      share = newShare;
    }

    // Generate share URL - use /share/folder/:token for folders
    const shareUrl = `${window.location.origin}/share/folder/${share.share_token}`;

    return {
      share,
      shareUrl,
    };
  }

  /**
   * Get folder share by folder ID
   */
  async getFolderShare(folderId: string): Promise<FolderShare | null> {
    const user = useAuthStore.getState().user;
    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('folder_shares')
      .select('*')
      .eq('folder_id', folderId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    if (error || !data) {
      return null;
    }

    return data as FolderShare;
  }

  /**
   * Deactivate a folder share
   */
  async deactivateFolderShare(folderId: string): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Check if account is manually suspended (without download access)
    await throwIfManuallySuspended();

    const { error } = await supabase
      .from('folder_shares')
      .update({ is_active: false })
      .eq('folder_id', folderId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to deactivate share: ${error.message}`);
    }
  }

  /**
   * Generate a random token (fallback)
   */
  private generateToken(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').substring(0, 32);
  }
}

// Export singleton instance
export const folderSharingService = new FolderSharingService();










